(() => {
  if (window.MonoreeRestockStatus) {
    window.MonoreeRestockStatus.refresh(document);
    return;
  }

  const DATA_SELECTOR = 'script[data-monoree-restock-variants]';
  const CARD_SELECTOR = '[data-monoree-delivery-card]';
  const RESTOCK_CLASS = 'monoree-restock-delivery-only';
  const selections = new WeakMap();
  const dataCache = new WeakMap();

  const readData = (productInfo) => {
    const script = productInfo?.querySelector(DATA_SELECTOR);
    if (!script) return null;
    const cached = dataCache.get(script);
    if (cached?.source === script.textContent) return cached;

    try {
      const variants = JSON.parse(script.textContent || '[]');
      if (!Array.isArray(variants)) return null;
      const data = {
        script,
        source: script.textContent,
        selectedId: String(script.dataset.selectedVariantId || ''),
        fallback: script.dataset.restockFallback || '',
        variants: new Map(variants.map((variant) => [String(variant.id), variant])),
      };
      dataCache.set(script, data);
      return data;
    } catch (error) {
      console.error('MONOREE restock data could not be parsed.', error);
      return null;
    }
  };

  const selectedVariant = (productInfo, data) => {
    const picker = productInfo.querySelector('variant-selects, variant-radios');
    if (!picker) return null;
    const groups = Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown'));
    if (!groups.length) return null;
    const options = groups.map((group) => {
      const input = group.querySelector('input[type="radio"]:checked');
      const option = group.querySelector('select')?.selectedOptions[0];
      return input?.value ?? (option && !option.disabled ? option.value : null);
    });
    if (options.some((value) => value === null)) return null;
    return Array.from(data.variants.values()).find((variant) =>
      Array.isArray(variant.options) && variant.options.length === options.length &&
      variant.options.every((value, index) => String(value) === options[index])
    ) || null;
  };

  const update = (productInfo, explicitVariant) => {
    if (!productInfo) return;
    const data = readData(productInfo);
    const delivery = productInfo.querySelector(CARD_SELECTOR);
    if (!data || !delivery) return;

    const selectedId = selections.has(productInfo) ? selections.get(productInfo) : data.selectedId;
    if (explicitVariant && String(explicitVariant.id || '') !== selectedId) return;
    const variant = data.variants.get(selectedId);
    const unavailable = explicitVariant ? explicitVariant.available === false : variant?.available !== true;
    const recommendation = productInfo.querySelector('monoree-ring-recommendation');
    const hasNearby = unavailable && typeof recommendation?.recommendations === 'function' &&
      recommendation.recommendations().length > 0;
    const label = delivery.querySelector('[data-monoree-restock-delivery-label]');
    if (unavailable && label) label.textContent = variant?.label || data.fallback;
    delivery.hidden = hasNearby;
    delivery.classList.toggle(RESTOCK_CLASS, unavailable);
    if (unavailable) delivery.open = false;
    delivery.querySelectorAll('summary').forEach((summary) => {
      if (unavailable) {
        summary.setAttribute('aria-disabled', 'true');
        summary.setAttribute('aria-expanded', 'false');
        summary.setAttribute('tabindex', '-1');
      } else {
        summary.removeAttribute('aria-disabled');
        summary.setAttribute('aria-expanded', String(delivery.open));
        if (summary.getAttribute('tabindex') === '-1') summary.removeAttribute('tabindex');
      }
    });
  };

  const refresh = (root = document) => {
    if (root.matches?.('product-info')) update(root);
    root.querySelectorAll?.('product-info').forEach((productInfo) => update(productInfo));
  };

  window.MonoreeRestockStatus = { refresh, update };

  document.addEventListener('change', (event) => {
    if (!event.target.closest?.('variant-selects, variant-radios')) return;
    const productInfo = event.target.closest('product-info');
    const data = readData(productInfo);
    if (!data) return;
    selections.set(productInfo, String(selectedVariant(productInfo, data)?.id || ''));
    update(productInfo);
  }, true);

  const preventExpansion = (event) => {
    if (event.type === 'keydown' && !['Enter', ' ', 'Spacebar'].includes(event.key)) return;
    const summary = event.target.closest?.('summary');
    const delivery = summary?.closest(CARD_SELECTOR);
    if (!delivery?.classList.contains(RESTOCK_CLASS)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    delivery.open = false;
  };

  document.addEventListener('click', preventExpansion, true);
  document.addEventListener('keydown', preventExpansion, true);
  document.addEventListener('beforetoggle', (event) => {
    if (event.target.matches?.(CARD_SELECTOR) && event.target.classList.contains(RESTOCK_CLASS) &&
        event.newState === 'open') event.preventDefault();
  }, true);
  document.addEventListener('toggle', (event) => {
    if (event.target.matches?.(CARD_SELECTOR) && event.target.classList.contains(RESTOCK_CLASS) &&
        event.target.open) event.target.open = false;
  }, true);

  document.addEventListener('product-info:loaded', (event) => update(event.target));
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));

  const initialize = () => {
    refresh(document);
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const sectionId = String(data?.sectionId || '');
      const productInfo = Array.from(document.querySelectorAll('product-info')).find((node) =>
        String(node.dataset.originalSection || node.dataset.section || '') === sectionId
      );
      const current = readData(productInfo);
      if (!current) return;
      const selectedId = selections.has(productInfo) ? selections.get(productInfo) : current.selectedId;
      if (String(data?.variant?.id || '') !== selectedId) return;
      const source = data.html?.querySelector(DATA_SELECTOR);
      if (source) {
        current.script.textContent = source.textContent;
        dataCache.delete(current.script);
      }
      update(productInfo, data.variant);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();

